import { inject, Injectable, Injector } from '@angular/core';
import { IndividualConfig, ToastrService } from 'ngx-toastr';
import { ActiveToast } from 'ngx-toastr/toastr/toastr.service';
import { ILocalizedToasterService } from 'shared-components';

import { TranslationService } from 'app/core/internationalization/translation.service';
import { A11yToastService } from 'app/shared/accessibility/services/a11y-toast.service';

type ToastType = 'success' | 'info' | 'error' | 'warning';

@Injectable({
  providedIn: 'root'
})
export class LocalizedToasterService implements ILocalizedToasterService {
  private readonly toastr = inject(ToastrService);
  private readonly injector = inject(Injector);
  private readonly a11yToastService = inject(A11yToastService);

  private translate(value: string): string {
    return value ? this.injector.get(TranslationService).instant(value) : '';
  }

  public pop(type: ToastType, title: string, body = '', config?: Partial<IndividualConfig>): ActiveToast<any> {
    const toast = this.toastr[type](this.translate(body), this.translate(title), { positionClass: 'toast-bottom-right', ...config });
    this.setDataTestId(toast, `toast-${type}`);
    return toast;
  }

  public success(title: string, body?: string, config?: Partial<IndividualConfig>): ActiveToast<any> {
    return this.pop('success', title, body, config);
  }

  public info(title: string, body?: string, config?: Partial<IndividualConfig>): ActiveToast<any> {
    return this.pop('info', title, body, config);
  }

  public error(title: string, body?: string, config?: Partial<IndividualConfig>): ActiveToast<any> {
    return this.pop('error', title, body, config);
  }

  public warn(title: string, body?: string, config?: Partial<IndividualConfig>): ActiveToast<any> {
    return this.pop('warning', title, body, config);
  }

  public persistedError(title: string, body?: string): ActiveToast<any> {
    const toast = this.toastr.error(body, title, { disableTimeOut: true });
    this.setDataTestId(toast, 'toast-persisted-error');

    const toastElement = toast.portal?.location?.nativeElement as HTMLElement | undefined;
    this.a11yToastService.applyToastA11y(
      toastElement,
      'error',
      (callback) => toast.onHidden?.subscribe(callback)
    );

    return toast;
  }

  public clear(toastId?: number): void {
    this.toastr.clear(toastId);
  }

  private setDataTestId(toast: ActiveToast<any>, testId: string): void {
    toast.portal?.location?.nativeElement?.setAttribute('data-testid', testId);
  }

  public khError(data: any): ActiveToast<any> {
    let message = Array.isArray(data?.error?.Errors) && data.error.Errors.length > 0 ? data.error.Errors[0] : '';
    if (!message) {
      message = data?.ExceptionMessage ?? '';
    }

    return this.error('Error', message);
  }
}
